import { truncate } from "lodash";
import { useMemo, useState } from "react";

import Button from "@components/button/button";
import MotionDiv from "@components/motion/motion-div";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";

import { nineBoxBg, nineBoxTextColor } from "./helpers";

const NineBoxCell = ({
  count,
  label,
  totalAssessments,
  isFiltered,
  onApplyFilter,
  onRemoveFilter,
}: {
  label?: string;
  count: number;
  totalAssessments: number;
  isFiltered: boolean;
  onApplyFilter: () => void;
  onRemoveFilter: () => void;
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const percentage =
    totalAssessments === 0 ? 0 : Math.floor((count / totalAssessments) * 100);

  const content = useMemo(() => {
    const render = (
      <div className="flex flex-col items-center">
        {label && (
          <div
            className="text-center text-sm"
            style={{ color: nineBoxTextColor(percentage) }}
          >
            {truncate(label, { length: 70 })}
          </div>
        )}
        <div
          className="flex items-center gap-4"
          style={{ color: nineBoxTextColor(percentage) }}
        >
          <div className="font-bold text-2xl">{count}</div>
          <div className="text-sm">
            ({percentage}
            %)
          </div>
        </div>
      </div>
    );
    if (!isHovered || isFiltered || count === 0) {
      return render;
    }
    return (
      <MotionDiv
        animate={{ translateY: 0 }}
        initial={{ translateY: 10 }}
        transition={{ duration: 0.1 }}
      >
        {render}
      </MotionDiv>
    );
  }, [count, isFiltered, isHovered, label, percentage]);

  return (
    <div
      aria-label="Assessment report nine box cell"
      className="h-40 relative flex flex-col items-center gap-2 justify-center rounded-lg p-6 @container/nineboxcell"
      style={{ backgroundColor: nineBoxBg(percentage) }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {content}
      {isHovered && count > 0 && !isFiltered && (
        <MotionDiv
          animate={{ opacity: 1 }}
          initial={{ opacity: 0 }}
          transition={{ duration: 0.1 }}
          className="w-full flex justify-center"
        >
          <Button
            onClick={onApplyFilter}
            className="shrink-0 flex items-center gap-1 max-w-full"
          >
            <TopicflowIcon type="filter" className="h-5 w-5 shrink-0" />
            <div className="flex-1 min-w-0 truncate" title="Apply Filter">
              <span className="hidden @[120px]/nineboxcell:inline">Apply </span>
              Filter
            </div>
          </Button>
        </MotionDiv>
      )}
      {isFiltered && (
        <Button
          onClick={onRemoveFilter}
          className="shrink-0 flex items-center gap-1 max-w-full"
        >
          <div className="flex-1 min-w-0 truncate" title="Remove Filter">
            Filter Applied
          </div>
          <TopicflowIcon type="close" className="h-5 w-5 shrink-0" />
        </Button>
      )}
    </div>
  );
};

export default NineBoxCell;
